/**
 * Static-credential connect dialog — the member-facing input for connections
 * whose secret the member supplies directly (no OAuth round-trip): `bearer` /
 * `api_key` (one token) and `basic_auth` (username + password).
 *
 * Submits through the unified `/me/connections/{provider}/complete` endpoint as
 * `{ fields }`, which the static-credentials handler stores encrypted as the
 * link's token. The agent then reaches the API via the connection's
 * `{{PREFIX_TOKEN}}` / `{{PREFIX_API_URL}}` placeholders.
 */

import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  meConnectionsService,
  type ConnectionEnvironment,
  type MeConnection,
} from "@/services/me-connections-service";

interface ConnectStaticCredentialDialogProps {
  conn: MeConnection;
  onClose: () => void;
  onConnected: () => void;
}

export function ConnectStaticCredentialDialog({
  conn,
  onClose,
  onConnected,
}: ConnectStaticCredentialDialogProps) {
  const isBasic = conn.connectKind === "basic_auth";
  const [value, setValue] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [accountLabel, setAccountLabel] = useState("");
  const [description, setDescription] = useState("");
  const [environment, setEnvironment] = useState<ConnectionEnvironment>("production");
  const [isDefault, setIsDefault] = useState(conn.linkedAccounts.length === 0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ready =
    accountLabel.trim().length > 0 &&
    (isBasic ? username.trim() && password : value.trim().length > 0);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const fields = isBasic
        ? { username: username.trim(), password }
        : { value: value.trim() };
      const res = await meConnectionsService.complete(conn.provider, {
        fields,
        profile: {
          accountLabel: accountLabel.trim(),
          description: description.trim(),
          environment,
          isDefault,
        },
      });
      if (!res.success) throw new Error("Could not save the credential.");
      toast.success(`${conn.displayName} connected`);
      onConnected();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not connect.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Connect {conn.displayName}</DialogTitle>
          <DialogDescription>
            {isBasic
              ? "Enter the username and password for this service. We store them encrypted and use them only when an agent calls this API on your behalf."
              : "Paste your API token for this service. We store it encrypted and send it only when an agent calls this API on your behalf."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="sc-label">Account name</Label>
            <Input
              id="sc-label"
              value={accountLabel}
              onChange={(e) => setAccountLabel(e.target.value)}
              placeholder="Production CRM API"
              disabled={busy}
              autoComplete="off"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sc-description">Description for agents</Label>
            <Textarea
              id="sc-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Use this for live customer and deal data."
              disabled={busy}
              rows={3}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Environment</Label>
            <Select
              value={environment}
              onValueChange={(value) => setEnvironment(value as ConnectionEnvironment)}
              disabled={busy}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="production">Production</SelectItem>
                <SelectItem value="staging">Staging</SelectItem>
                <SelectItem value="development">Development</SelectItem>
                <SelectItem value="sandbox">Sandbox</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isBasic ? (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="sc-username">Username</Label>
                <Input
                  id="sc-username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={busy}
                  autoComplete="off"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sc-password">Password</Label>
                <Input
                  id="sc-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={busy}
                  autoComplete="new-password"
                />
              </div>
            </>
          ) : (
            <div className="space-y-1.5">
              <Label htmlFor="sc-token">API token</Label>
              <Input
                id="sc-token"
                type="password"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="Paste your token"
                disabled={busy}
                autoComplete="new-password"
              />
            </div>
          )}

          <label className="flex items-center gap-2 text-sm text-foreground">
            <Checkbox
              checked={isDefault}
              onCheckedChange={(checked) => setIsDefault(checked === true)}
              disabled={busy}
            />
            Use by default for {conn.displayName}
          </label>

          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy || !ready}>
            {busy ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
            Connect
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
